import { differenceWith, intersectionBy, intersectionWith } from "lodash";

import { DataFieldApi } from "lib/api";
import { DataFieldTemplate, MultipleItemRequirement, OrderedDataFieldTemplate, Requirement, RequirementItem, SingleItemRequirement, DataField } from "lib/data/model";
import { MultipleItemRequirementType, Requiredness } from "lib/data/model/enumerations";

export class DataFieldTemplateService {

	public static getTemplatesFromRequirements = (requirements: Array<Requirement>)
		: DataFieldTemplate[] => {

		// Initialize Template Array
		const templates: DataFieldTemplate[] = [];

		// Loop Through Requirements
		requirements.forEach((requirement) => {

			// Check Requirement Type
			switch (requirement.$type) {

				case SingleItemRequirement.$type.DataFieldTemplate:

					// Store Requirement
					const singleItemRequirement = requirement as SingleItemRequirement<DataFieldTemplate>;

					// Add Template To Array
					templates.push(singleItemRequirement.item.item);

					// Exit Switch
					break;

				case MultipleItemRequirement.$type.DataFieldTemplate:

					// Store Requirement
					const multipleItemRequirement = requirement as MultipleItemRequirement<DataFieldTemplate>;

					// Loop Through Requirement Items
					multipleItemRequirement.items.forEach((item) => {

						// Add Template To Array
						templates.push(item.item);
					});

					// Exit Switch
					break;
			}
		});

		// Return Templates
		return templates;
	}

	public static getRequirementFromTemplate = (requirements: Array<Requirement>, template: DataFieldTemplate) : Requirement | undefined => {

		if (requirements === undefined) {

			requirements = [];
		}

		// Loop Through Requirements
		for (const requirement of requirements) {

			// Check Requirement Type
			switch (requirement.$type) {

				case SingleItemRequirement.$type.DataFieldTemplate:

					// Store Requirement
					const singleItemRequirement = requirement as SingleItemRequirement<DataFieldTemplate>;

					// Check Requirement's Template
					if (singleItemRequirement.item.item.field.id === template.field.id) {

						// Return Requirement
						return singleItemRequirement;
					}

					// Exit Switch
					break;

				case MultipleItemRequirement.$type.DataFieldTemplate:

					// Store Requirement
					const multipleItemRequirement = requirement as MultipleItemRequirement<DataFieldTemplate>;

					// Loop Through Requirement Items
					for (const item of multipleItemRequirement.items) {

						// Check Requirement Item's Template
						if (item.item.field.id === template.field.id) {

							// Return Requirement
							return multipleItemRequirement;
						}
					}

					// Exit Switch
					break;
			}
		}

		// Return Nothing
		return undefined;
	}

	public static getRequirementItemFromTemplate = (requirements: Array<Requirement>, template: DataFieldTemplate)
		: RequirementItem<DataFieldTemplate> | undefined => {

		// Loop Through Requirements
		for (const requirement of requirements) {

			// Check Requirement Type
			switch (requirement.$type) {

				case SingleItemRequirement.$type.DataFieldTemplate:

					// Store Requirement
					const singleItemRequirement = requirement as SingleItemRequirement<DataFieldTemplate>;

					// Check Requirement's Template
					if (singleItemRequirement.item.item.field.id === template.field.id) {

						// Return Requirement Item
						return singleItemRequirement.item;
					}

					// Exit Switch
					break;

				case MultipleItemRequirement.$type.DataFieldTemplate:

					// Store Requirement
					const multipleItemRequirement = requirement as MultipleItemRequirement<DataFieldTemplate>;

					// Loop Through Requirement Items
					for (const item of multipleItemRequirement.items) {

						// Check Requirement Item's Template
						if (item.item.field.id === template.field.id) {

							// Return Requirement Item
							return item;
						}
					}

					// Exit Switch
					break;
			}
		}

		// Return Nothing
		return undefined;
	}

	public static getAddableRequirements =
		async (usedFieldTemplates: OrderedDataFieldTemplate[], requirements?: Array<Requirement>) => {

			// Clear Anything Previous
			const addableItems: OrderedDataFieldTemplate[] = [];

			if (requirements !== undefined) {

				// Loop Through Requirements
				for (const requirement of requirements) {

					// Switch On Requirement Type
					switch (requirement.$type) {

						case SingleItemRequirement.$type.DataFieldTemplate:

							// Cast Requirement
							const singleItemRequirement = requirement as SingleItemRequirement<DataFieldTemplate>;

							// Check If Repeatable
							if (singleItemRequirement.isRepeatable) {

								// Get Requirement Template(s)
								const requirementTemplates = DataFieldTemplateService.getTemplatesFromRequirements([singleItemRequirement]);

								// Add Template Data Field To Addable List
								addableItems.push(...requirementTemplates.map((dataFieldTemplate) => {

									// Get Requirement Item For Template
									const requirementItem = DataFieldTemplateService.getRequirementItemFromTemplate([singleItemRequirement], dataFieldTemplate);

									// Initialize Ordered Item
									const orderedItem = new OrderedDataFieldTemplate();
									orderedItem.order = (requirementItem !== undefined ? requirementItem.initialOrder : 0);
									orderedItem.item = dataFieldTemplate;

									// Return Ordered Item
									return orderedItem;
								}));
							}
							else {

								// Check Values For Matching Data Field
								if (usedFieldTemplates.find((value) => value.item.field.id === singleItemRequirement.item.item.field.id) === undefined) {

									// Initialize Ordered Item
									const orderedItem = new OrderedDataFieldTemplate();
									orderedItem.order = singleItemRequirement.item.initialOrder;
									orderedItem.item = singleItemRequirement.item.item;

									// Push Ordered Item
									addableItems.push(orderedItem);
								}
							}

							// Exit Requirement Type Switch
							break;

						case MultipleItemRequirement.$type.DataFieldTemplate:

							// Cast Requirement
							const multipleItemRequirement = requirement as MultipleItemRequirement<DataFieldTemplate>;

							// Get Fields
							const requirementFields = multipleItemRequirement.items.map((i) => i.item);

							// Get All Fulfilled Fields
							const fulfilledFields = intersectionWith(usedFieldTemplates, requirementFields, (a, b) => a.item.field.id === b.field.id);

							// Switch On Multiple Item Requirement Type
							switch (multipleItemRequirement.requirementType) {

								case MultipleItemRequirementType.AtLeast:

									addableItems.push(...differenceWith(requirementFields, usedFieldTemplates, (a, b) => a.field.id === b.item.field.id).map((dataFieldTemplate) => {

										// Get Requirement Item For Template
										const requirementItem = DataFieldTemplateService.getRequirementItemFromTemplate([multipleItemRequirement], dataFieldTemplate);

										// Initialize Ordered Item
										const orderedItem = new OrderedDataFieldTemplate();
										orderedItem.order = (requirementItem !== undefined ? requirementItem.initialOrder : 0);
										orderedItem.item = dataFieldTemplate;

										// Return Ordered Item
										return orderedItem;
									}));

									// Exit Multiple Item Requirement Type Switch
									break;

								case MultipleItemRequirementType.AtMost:

									// Check Fulfilled Fields
									if (fulfilledFields.length < multipleItemRequirement.fulfillmentCount) {

										addableItems.push(...differenceWith(requirementFields, usedFieldTemplates, (a, b) => a.field.id === b.item.field.id).map((dataFieldTemplate) => {

											// Get Requirement Item For Template
											const requirementItem = DataFieldTemplateService.getRequirementItemFromTemplate([multipleItemRequirement], dataFieldTemplate);

											// Initialize Ordered Item
											const orderedItem = new OrderedDataFieldTemplate();
											orderedItem.order = (requirementItem !== undefined ? requirementItem.initialOrder : 0);
											orderedItem.item = dataFieldTemplate;

											// Return Ordered Item
											return orderedItem;
										}));
									}

									// Exit Multiple Item Requirement Type Switch
									break;

								case MultipleItemRequirementType.Exactly:

									// Check Fulfilled Fields
									if (fulfilledFields.length < multipleItemRequirement.fulfillmentCount) {

										addableItems.push(...differenceWith(requirementFields, usedFieldTemplates, (a, b) => a.field.id === b.item.field.id).map((dataFieldTemplate) => {

											// Get Requirement Item For Template
											const requirementItem = DataFieldTemplateService.getRequirementItemFromTemplate([multipleItemRequirement], dataFieldTemplate);

											// Initialize Ordered Item
											const orderedItem = new OrderedDataFieldTemplate();
											orderedItem.order = (requirementItem !== undefined ? requirementItem.initialOrder : 0);
											orderedItem.item = dataFieldTemplate;

											// Return Ordered Item
											return orderedItem;
										}));
									}

									// Exit Multiple Item Requirement Type Switch
									break;

								case MultipleItemRequirementType.All:

									// Check Fulfilled Fields
									if (fulfilledFields.length < multipleItemRequirement.items.length) {

										addableItems.push(...differenceWith(requirementFields, usedFieldTemplates, (a, b) => a.field.id === b.item.field.id).map((dataFieldTemplate) => {

											// Get Requirement Item For Template
											const requirementItem = DataFieldTemplateService.getRequirementItemFromTemplate([multipleItemRequirement], dataFieldTemplate);

											// Initialize Ordered Item
											const orderedItem = new OrderedDataFieldTemplate();
											orderedItem.order = (requirementItem !== undefined ? requirementItem.initialOrder : 0);
											orderedItem.item = dataFieldTemplate;

											// Return Ordered Item
											return orderedItem;
										}));
									}

									// Exit Multiple Item Requirement Type Switch
									break;
							}

							// Exit Requirement Type Switch
							break;
					}
				}
			}
			else {

				// Get All Available Fields
				const dataFields = await DataFieldApi.getDataFields();

				// Create Templates For All Data Fields
				const dataFieldTemplates = dataFields.map((field) => {

					const newTemplate = new DataFieldTemplate();
					newTemplate.field = field;
					newTemplate.name = "";
					newTemplate.stringPrefix = "";
					newTemplate.stringAppendage = "";

					return newTemplate;
				});

				// Add All Fields
				addableItems.push(...dataFieldTemplates.map((template) => {

					// Initialize Ordered Item
					const orderedItem = new OrderedDataFieldTemplate();
					orderedItem.order = 0;
					orderedItem.item = template;

					// Return Ordered Item
					return orderedItem;
				}));
			}

			// Return Addable Items
			return addableItems;
		}

	public static isRequirementSatisfied = (requirement: Requirement, appliedTemplates: DataFieldTemplate[]): boolean => {

		let isRequirementSatisfied = false;

		// If the requirement is a single item requirement
		if (requirement.$type === SingleItemRequirement.$type.DataFieldTemplate) {

			const singleItemRequirementFulfilled = appliedTemplates.find((template) => {
				return template.field.id === (requirement as SingleItemRequirement<DataFieldTemplate>).id;
			});

			// If the single item requirement hasn't been applied, or the requiredness is optional, it has been satisfied
			isRequirementSatisfied = singleItemRequirementFulfilled !== undefined || requirement.requiredness === Requiredness.OPTIONAL;
		}

		// If the requirement is a multiple item requirement
		else if (requirement.$type === MultipleItemRequirement.$type.DataFieldTemplate) {

			const multipleItemRequirement = (requirement as MultipleItemRequirement<DataFieldTemplate>);

			// Find the number/list of fulfilled requirements
			const dataFieldTemplateIntersection = intersectionBy(multipleItemRequirement.items.map((i) => i.item), appliedTemplates, "field");
			const fulfillmentCount = dataFieldTemplateIntersection.length;

			// Relative to the requirement type, perform a check
			switch (multipleItemRequirement.requirementType) {

				case MultipleItemRequirementType.AtLeast:

					isRequirementSatisfied = fulfillmentCount >= multipleItemRequirement.fulfillmentCount;
					break;

				case MultipleItemRequirementType.All:

					isRequirementSatisfied = fulfillmentCount === multipleItemRequirement.items.length;
					break;

				case MultipleItemRequirementType.AtMost:

					isRequirementSatisfied = fulfillmentCount <= multipleItemRequirement.fulfillmentCount;
					break;

				case MultipleItemRequirementType.Exactly:

					isRequirementSatisfied = fulfillmentCount === multipleItemRequirement.fulfillmentCount;
					break;
			}
		}


		return isRequirementSatisfied;
	}

	public static isDataFieldRequired = (requirements: Array<SingleItemRequirement<DataFieldTemplate>>, field: DataField): boolean => {

		const matchingTemplate = requirements.find((requirement) => requirement.item.item.field.id === field.id);

		if (matchingTemplate !== undefined && matchingTemplate.requiredness === Requiredness.REQUIRED) {

			return true;
		}

		return false;
	}
}
